import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Badge } from '@/app/components/ui/badge'
import { Button } from '@/app/components/ui/button'
import { Textarea } from '@/app/components/ui/textarea'
import { PendingTransfer } from '@/app/services/inventory.service'

interface ReceiveConfirmModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedTransfer: PendingTransfer | null
  onConfirm: (
    transfer: PendingTransfer,
    status: Record<
      string,
      {
        status: 'received' | 'not_received'
        quantity: number
        reason: string
      }
    >,
  ) => void
}

export function ReceiveConfirmModal({
  open,
  onOpenChange,
  selectedTransfer,
  onConfirm,
}: ReceiveConfirmModalProps) {
  const [receiveProductsStatus, setReceiveProductsStatus] = useState<
    Record<
      string,
      {
        status: 'received' | 'not_received'
        quantity: number
        reason: string
      }
    >
  >({})

  useEffect(() => {
    if (open && selectedTransfer) {
      const initialStatus: Record<
        string,
        {
          status: 'received' | 'not_received'
          quantity: number
          reason: string
        }
      > = {}
      selectedTransfer.products.forEach((product, idx) => {
        initialStatus[idx.toString()] = {
          status: 'received',
          quantity: product.quantity,
          reason: '',
        }
      })
      setReceiveProductsStatus(initialStatus)
    }
  }, [open, selectedTransfer])

  const handleConfirm = () => {
    if (selectedTransfer) {
      onConfirm(selectedTransfer, receiveProductsStatus)
    }
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Confirmar Recepción de Transferencia</DialogTitle>
          <DialogDescription className="sr-only">
            Revisar y confirmar los productos recibidos de la transferencia
          </DialogDescription>
        </DialogHeader>
        {selectedTransfer && (
          <div className="space-y-6">
            {/* Transfer Details */}
            <div className="rounded-lg bg-slate-50 p-4">
              <h4 className="mb-3 text-sm font-medium text-slate-900">
                Detalles de la Transferencia
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">ID Transferencia:</span>
                  <span className="font-medium text-slate-900">
                    {selectedTransfer.id}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Fecha:</span>
                  <span className="font-medium text-slate-900">
                    {selectedTransfer.date}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Desde:</span>
                  <span className="font-medium text-slate-900">
                    {selectedTransfer.fromBranch}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Hacia:</span>
                  <span className="font-medium text-slate-900">
                    {selectedTransfer.toBranch}
                  </span>
                </div>
              </div>
            </div>

            {/* Products List */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-900">
                Productos a Recibir
              </h4>
              <p className="text-xs text-slate-600">
                Indica el estado de cada producto recibido
              </p>
              <div className="space-y-3">
                {selectedTransfer.products.map((product, idx) => {
                  const status = receiveProductsStatus[idx.toString()]
                  return (
                    <div
                      key={idx}
                      className="space-y-3 rounded-lg border border-slate-200 p-3"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {product.name}
                          </p>
                          <p className="text-xs text-slate-600">
                            Cantidad solicitada: {product.quantity} unidades
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={
                            status?.status === 'received'
                              ? 'border-green-200 bg-green-50 text-green-700'
                              : 'border-red-200 bg-red-50 text-red-700'
                          }
                        >
                          {status?.status === 'received' && 'Recibido'}
                          {status?.status === 'not_received' && 'No recibido'}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            setReceiveProductsStatus({
                              ...receiveProductsStatus,
                              [idx.toString()]: {
                                status: 'received',
                                quantity: product.quantity,
                                reason: '',
                              },
                            })
                          }}
                          className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${
                            status?.status === 'received'
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          Recibido
                        </button>
                        <button
                          onClick={() => {
                            setReceiveProductsStatus({
                              ...receiveProductsStatus,
                              [idx.toString()]: {
                                status: 'not_received',
                                quantity: 0,
                                reason: '',
                              },
                            })
                          }}
                          className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${
                            status?.status === 'not_received'
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          No recibido
                        </button>
                      </div>

                      {status?.status === 'not_received' && (
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-700">
                            Motivo (del remitente)
                          </label>
                          <Textarea
                            value={status.reason}
                            onChange={(e) => {
                              setReceiveProductsStatus({
                                ...receiveProductsStatus,
                                [idx.toString()]: {
                                  ...status,
                                  reason: e.target.value,
                                },
                              })
                            }}
                            placeholder="Ingresa el motivo..."
                            rows={2}
                            className="text-xs"
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Summary */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="mb-2 text-sm font-medium text-blue-900">
                Resumen de Recepción
              </p>
              <div className="space-y-1 text-sm text-blue-800">
                <p>
                  Productos recibidos:{' '}
                  {
                    Object.values(receiveProductsStatus).filter(
                      (v) => v.status === 'received',
                    ).length
                  }
                </p>
                {Object.values(receiveProductsStatus).filter(
                  (v) => v.status === 'not_received',
                ).length > 0 && (
                  <p className="text-red-700">
                    Productos no recibidos:{' '}
                    {
                      Object.values(receiveProductsStatus).filter(
                        (v) => v.status === 'not_received',
                      ).length
                    }
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button onClick={handleConfirm} className="flex-1">
                Confirmar Recepción
              </Button>
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
